import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@sc/data-access-assets';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  Appointment,
  Car,
  Claim,
  CLIENT_APP_ID,
  ClientAppId,
  Customer,
  Dossier,
  getGlassNameKey,
  getOperationWithGlassKey,
  RequestAppointment,
  RoutingName,
} from '@sc/data-access-models';
import { LocaleDatePipe, TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { ButtonComponent, IconComponent } from '@sc/ui-components';
import { map, Observable } from 'rxjs';
import {
  getAppointmentApplicableFilter,
  isDossierAppointmentAbandoned,
  isDossierAppointmentCancelled,
  isDossierAppointmentComplete,
  isDossierAppointmentNew,
  isDossierAppointmentNoShow,
  isDossierRequestAppointment,
} from '../../types';

@Component({
  selector: 'sc-dossiers-search-result',
  styleUrls: ['./dossiers-search-result.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgFor,
    IconComponent,
    NgClass,
    NgIf,
    ButtonComponent,
    MatTooltipModule,
    AsyncPipe,
    TranslateModule,
    LocaleDatePipe,
  ],
  template: `
    <div *ngFor="let dossier of dossiers" class="dossier-container">
      <div class="dossier-infos">
        <div class="dossier-infos-id sc-design-text-2">
          {{
            'DossiersSearchResult.dossierIdNotFound'
              | translate
                : {
                    id: dossier.id,
                    customerFullName: formatCustomerFullNameText(
                      dossier.customer
                    )
                  }
          }}
          <span class="dossier-infos-date sc-design-text-3">
            ({{ dossier.createDate | localeDate: 'shortDate' | async }} -
            {{ dossier.createDate | localeDate: 'shortTime' | async }})
          </span>
        </div>
        <div class="dossier-infos-license-plate sc-design-text-3">
          <sc-icon name="plaque" [inline]="true"></sc-icon>
          <span>{{ formatLicensePlateText(dossier.claim?.car) }}</span>
        </div>
        <div class="dossier-infos-manufacturer sc-design-text-3">
          <sc-icon name="car" [inline]="true"></sc-icon>
          <span>{{ formatManufacturerText(dossier.claim) }} </span>
        </div>
        <div class="dossier-infos-operation sc-design-text-3">
          <sc-icon name="accident" [inline]="true"></sc-icon>
          <span>{{ formatRequiredOperationText(dossier.claim) }} </span>
        </div>
      </div>

      <div
        class="appointment-infos"
        [ngClass]="getAppointmentColorClass(dossier)"
      >
        <div class="appointment-infos-header">
          <span
            [ngClass]="getAppointmentColorClass(dossier)"
            class="dossier-progress sc-design-caption"
            >{{ getProgressLabel(dossier) }}</span
          >
          <span class="fitter-origin sc-design-caption"
            ><sc-icon [name]="getOriginIcon(dossier)"></sc-icon
            >{{ getOriginValues(dossier) | translate }}</span
          >
          <span
            *ngIf="getDisplayStep(dossier)?.length && isInProgress(dossier)"
            class="step-infos sc-design-caption"
            >{{ getDisplayStep(dossier) }}</span
          >
        </div>
        <div
          *ngIf="
            isDossierAppointmentNew(dossier) ||
            isDossierAppointmentComplete(dossier) ||
            isDossierRequestAppointment(dossier)
          "
          class="appointment-infos-body"
        >
          <div *ngIf="hasAppointmentOrRequest(dossier)">
            <span class="appointment-info-title sc-design-title-card"
              >{{
                isOutsideIntervention(dossier)
                  ? ('DossiersSearchResult.interventionAtHome' | translate)
                  : ('DossiersSearchResult.interventionInCenter' | translate)
              }}
              -
              {{
                getAppointmentBrandName(
                  dossier.appointment || dossier.requestAppointment
                )
              }}</span
            >
            <span
              *ngIf="!!dossier?.appointment?.time"
              class="appointment-info-item sc-design-text-3"
              ><sc-icon name="calendar_hour"></sc-icon>
              {{ dossier?.appointment?.time | localeDate: 'fullDate' | async }}
              {{
                dossier?.appointment?.time | localeDate: 'shortTime' | async
              }}</span
            >
            <span class="appointment-info-item sc-design-text-3"
              ><sc-icon name="map_location"></sc-icon>
              {{ getInterventionLocation(dossier) }}</span
            >
          </div>
          <div *ngIf="!hasAppointmentOrRequest(dossier)">
            <span class="appointment-info-title sc-design-title-card">{{
              'DossiersSearchResult.dossierWithoutApmt' | translate
            }}</span>
          </div>
        </div>
        <div
          class="appointment-infos-body"
          *ngIf="
            !isDossierAppointmentAbandoned(dossier) &&
            (isDossierAppointmentCancelled(dossier) ||
              isDossierAppointmentNoShow(dossier))
          "
        >
          <span class="appointment-info-title sc-design-title-card">
            {{ 'DossiersSearchResult.apmtCancelled' | translate }} -
            {{ getAppointmentBrandName(dossier.appointment) }}
          </span>
          <span class="sc-design-text-3">
            {{
              dossier.appointment?.comment
                ? (dossier.appointment?.comment | translate)
                : isDossierAppointmentNoShow(dossier)
                ? ('DossiersSearchResult.defaultNoShowComment' | translate)
                : ('DossiersSearchResult.defaultCancelComment' | translate)
            }}
          </span>
        </div>
        <div
          *ngIf="isDossierAppointmentAbandoned(dossier)"
          [ngClass]="{
            'appointment-infos-body-with-appointment':
              hasAppointmentOrRequest(dossier)
          }"
          class="appointment-infos-body sc-design-text-3"
        >
          <span class="appointment-info-title">{{
            'DossiersSearchResult.abandonmentComment'
              | translate
                : {
                    hasComment:
                      !!dossier.insurerData?.abandonmentComment?.trim(),
                    comment: dossier.insurerData?.abandonmentComment
                  }
          }}</span>
        </div>
      </div>

      <sc-button
        *ngIf="displayConsultBtn(dossier) | async"
        (click)="consult.emit(dossier)"
        [id]="'consultButton-' + dossier.id"
        leftIcon="folder"
      >
        {{ getButtonText(dossier) | translate }}
      </sc-button>

      <sc-icon
        *ngIf="userIsSuperAdmin$ | async"
        name="info"
        [matTooltip]="getAdminTooltipInformation(dossier)"
        matTooltipPosition="above"
        data-html="true"
        #tooltip="matTooltip"
      ></sc-icon>
    </div>
  `,
})
export class DossiersSearchResultComponent {
  @Input() dossiers: Dossier[] = [];
  @Output() consult = new EventEmitter<Dossier>();
  @Input() hasValidatedRequestApts = false;

  isDossierRequestAppointment = isDossierRequestAppointment;
  isDossierAppointmentNew = isDossierAppointmentNew;
  isDossierAppointmentAbandoned = isDossierAppointmentAbandoned;
  isDossierAppointmentCancelled = isDossierAppointmentCancelled;
  isDossierAppointmentNoShow = isDossierAppointmentNoShow;
  isDossierAppointmentComplete = isDossierAppointmentComplete;

  userIsInsurer$: Observable<boolean>;
  userIsSuperAdmin$: Observable<boolean>;

  constructor(
    private readonly translator: TranslatorService,
    public authService: AuthService,
    public timezoneService: TimezoneService,
    private readonly localeDatePipe: LocaleDatePipe,
    private readonly configurationService: ConfigurationService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    this.userIsInsurer$ = authService.userIsInsurer$();
    this.userIsSuperAdmin$ = authService.userIsSuperAdmin$();
  }

  formatCustomerFullNameText(customer: Customer): string {
    const name = [];

    if (customer.firstname) {
      name.push(this.capitalize(customer.firstname));
    }
    if (customer.lastname) {
      name.push(customer.lastname.toUpperCase());
    }

    return name.length
      ? name.join(' ')
      : this.translator.getInstantTranslation(
          'DossiersSearchResult.customerNotFound'
        );
  }

  formatLicensePlateText(car: Car): string {
    return (
      car?.licensePlateNumber ??
      this.translator.getInstantTranslation(
        'DossiersSearchResult.licensePlateNotFound'
      )
    );
  }

  formatManufacturerText(claim: Claim): string {
    const name = [];

    if (claim?.car?.manufacturer) {
      name.push(claim.car.manufacturer);
    }
    if (claim?.car?.model) {
      name.push(claim.car.model);
    }

    return name.length
      ? name.join(' ')
      : this.translator.getInstantTranslation(
          'DossiersSearchResult.manufacturerNotFound'
        );
  }

  formatRequiredOperationText(claim: Claim): string {
    if (claim?.glass) {
      return this.translator.getInstantTranslation(
        getOperationWithGlassKey(claim?.operationType),
        {
          glassName: this.translator
            .getInstantTranslation(getGlassNameKey(claim.glass))
            .toLowerCase(),
        }
      );
    }

    return this.translator.getInstantTranslation(
      'DossiersSearchResult.operationNotFound'
    );
  }

  capitalize(text: string): string {
    if (text?.trim()) {
      const words = text.trim().split(' ');

      return words
        .filter((word) => !!word)
        .map((word) => {
          return word[0].toUpperCase() + word.slice(1)?.toLowerCase();
        })
        .join(' ');
    }

    return text;
  }

  hasAppointmentOrRequest(dossier: Dossier) {
    return !!dossier?.appointment?.id || !!dossier?.requestAppointment;
  }

  isOutsideIntervention(dossier: Dossier) {
    return (
      dossier?.appointment?.outsideIntervention ??
      dossier?.requestAppointment?.outsideIntervention
    );
  }

  getInterventionLocation(dossier: Dossier) {
    return (
      dossier?.appointment?.interventionLocation?.address ||
      dossier?.requestAppointment?.interventionLocation?.address ||
      '-'
    );
  }

  getAppointmentColorClass(dossier: Dossier): string {
    return (
      {
        NEW: 'dossier-progress',
        COMPLETED: 'appointment-completed',
        CANCELLED: 'appointment-cancelled',
        ABANDONED: 'dossier-abandoned',
        REQUESTED: 'dossier-request',
        REQUESTED_AND_VALIDATED: 'dossier-request-validated',
      }[getAppointmentApplicableFilter(dossier)] ?? ''
    );
  }

  getAppointmentBrandName(
    appointment: Appointment | RequestAppointment
  ): string {
    return this.configurationService.getBrandName(appointment?.brandId);
  }

  displayConsultBtn(dossier: Dossier): Observable<boolean> {
    return this.userIsInsurer$.pipe(
      map((isInsurer) => {
        return (
          !!dossier.appointment?.id ||
          (isInsurer && getAppointmentApplicableFilter(dossier) === 'NEW') ||
          getAppointmentApplicableFilter(dossier) === 'ABANDONED' ||
          !!dossier?.requestAppointment
        );
      })
    );
  }

  getButtonText(dossier: Dossier): string {
    if (
      getAppointmentApplicableFilter(dossier) === 'NEW' &&
      !dossier?.requestAppointment
    ) {
      return 'DossiersSearchResult.continueBtn';
    }
    return 'DossiersSearchResult.consultBtn';
  }

  getAdminTooltipInformation(dossier: Dossier): string {
    return `${this.translator.getInstantTranslation(
      'DossiersSearchResult.adminTooltip.dateCreation'
    )}
    ${this.localeDatePipe.getInstantTransform(dossier.createDate, 'short')}

    ${this.translator.getInstantTranslation(
      'DossiersSearchResult.adminTooltip.searchAddress'
    )}
    ${
      dossier.searchCriteria?.address?.address ??
      this.translator.getInstantTranslation(
        'DossiersSearchResult.adminTooltip.emptySearchAddress'
      )
    }
    `;
  }

  getOriginIcon(dossier: Dossier): IconName {
    const origin =
      this.configurationService.getDossierGlobalOriginFromCreationData(
        dossier?.creationData,
        this.clientAppId
      );
    return (
      {
        MOBILE_APP: 'device',
        AGENDA: 'conveying',
        PARTNER: 'insurer',
        SITE_WEB: 'device',
        CALL_CENTER: 'phone_call',
        CHALLENGE: 'trophy',
        AICHAT: 'aichat',
      }[origin] ?? null
    );
  }

  getOriginValues(dossier: Dossier): string {
    return `GlobalApplicativeConstants.DossierOrigins.${this.configurationService.getDossierGlobalOriginFromCreationData(
      dossier?.creationData,
      this.clientAppId
    )}`;
  }

  /* * NOTE if B2B Steps will be implemented, translations should be adjusted
   * (Not forget to add B2B page traductions in GlobalApplicativeConstants.StepperTitles.pageTitles.)
   * In B2C, if other pages will be added in step management (pages like cancel or modification), traductions might be updated
   */
  getDisplayStep(dossier: Dossier) {
    const stepKey = this.configurationService
      ?.getConfig()
      ?.stepperPageMapping?.find((step) =>
        step?.pages.includes(dossier?.declarationPage as RoutingName)
      )?.stepKey;
    const steptrad = this.translator.getInstantTranslation(
      `GlobalApplicativeConstants.StepperTitles.CustomerStepper.${stepKey}`
    );
    const pageName = this.translator?.getInstantTranslation(
      `GlobalApplicativeConstants.StepperTitles.pageTitles.customer.${dossier?.declarationPage}`
    );

    return !!stepKey && !!dossier?.declarationPage
      ? steptrad + ' - ' + pageName
      : '';
  }

  isInProgress(dossier: Dossier) {
    return (
      (dossier?.state === 'INIT' ||
        dossier?.state === 'QUALIFIED' ||
        dossier?.state === 'SLOT_SEARCH') &&
      !isDossierRequestAppointment(dossier)
    );
  }

  getProgressLabel(dossier: Dossier) {
    return (
      {
        NEW: this.translator.getInstantTranslation(
          'DossiersSearchBar.dossierState.inProgress'
        ),
        COMPLETED: this.translator.getInstantTranslation(
          'DossiersSearchBar.dossierState.complete'
        ),
        CANCELLED: this.translator.getInstantTranslation(
          'DossiersSearchBar.dossierState.cancelled'
        ),
        ABANDONED: this.translator.getInstantTranslation(
          'DossiersSearchBar.dossierState.abandoned'
        ),
        REQUESTED: this.hasValidatedRequestApts
          ? this.translator.getInstantTranslation(
              'DossiersSearchBar.dossierState.requested'
            )
          : this.translator.getInstantTranslation(
              'DossiersSearchBar.dossierState.requestedGeneral'
            ),
        REQUESTED_AND_VALIDATED: this.translator.getInstantTranslation(
          'DossiersSearchBar.dossierState.requestedAndValidated'
        ),
      }[getAppointmentApplicableFilter(dossier)] ?? ''
    );
  }
}
